import { useState } from 'react'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { createCardmarketFilterUrl, fetchCardmarketPrices } from '@/services/cardmarketService'
import { STANDARD_CONDITIONS, type StandardCondition } from '@/types/inventory'
import type { CardmarketPrice, FullPokemonCard } from '@/types/pokemon'

interface CardmarketLivePricesProps {
  card: FullPokemonCard
}

function formatPrice(price: number): string {
  return `€${price.toFixed(2)}`
}

type GroupedCardmarketPrice = CardmarketPrice & { count: number }

const CONDITION_SORT_ALIASES = [['mint'], ['near mint', 'nearmint'], ['excellent'], ['good'], ['light played', 'lightplayed'], ['played'], ['poor']] as const

function getConditionSortRank(condition: string): number {
  const normalized = condition.trim().toLowerCase().replace(/\s+/g, ' ')
  const compact = normalized.replace(/\s+/g, '')
  const rank = CONDITION_SORT_ALIASES.findIndex((aliases) => aliases.some((alias) => normalized === alias || compact === alias.replace(/\s+/g, '')))
  return rank === -1 ? CONDITION_SORT_ALIASES.length : rank
}

function groupCardmarketPrices(prices: CardmarketPrice[]): GroupedCardmarketPrice[] {
  const groups = new Map<string, GroupedCardmarketPrice>()

  for (const entry of prices) {
    const key = `${entry.condition}\0${entry.language}\0${entry.price}`
    const existing = groups.get(key)
    if (existing) {
      existing.count += 1
    } else {
      groups.set(key, { ...entry, count: 1 })
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    const conditionDiff = getConditionSortRank(a.condition) - getConditionSortRank(b.condition)
    if (conditionDiff !== 0) return conditionDiff
    if (a.price !== b.price) return a.price - b.price
    return a.language.localeCompare(b.language)
  })
}

export function CardmarketLivePrices({ card }: CardmarketLivePricesProps) {
  const [condition, setCondition] = useState<StandardCondition>('NearMint')
  const [prices, setPrices] = useState<CardmarketPrice[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)

  const handleConditionChange = (value: StandardCondition) => {
    setCondition(value)
    setPrices(null)
    setHasLoaded(false)
    setError(null)
  }

  const handleLoadPrices = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await fetchCardmarketPrices(card.id, condition)
      setPrices(result)
      setHasLoaded(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prices')
      setPrices(null)
      setHasLoaded(false)
    } finally {
      setIsLoading(false)
    }
  }

  const groupedPrices = prices ? groupCardmarketPrices(prices) : null
  const lowestPrice = prices?.length ? Math.min(...prices.map((entry) => entry.price)) : null

  return (
    <div className="bg-blue-800 rounded-xl p-6 mb-6 text-white shadow-lg">
      <div className="flex flex-col gap-4 mb-5 pb-4 border-b border-white/20 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-2xl font-semibold m-0">🛒 Live Cardmarket Offers</h3>
          <p className="text-sm opacity-90 mt-1 mb-0">Current listings scraped from Cardmarket (min. {condition})</p>
        </div>

        {hasLoaded && lowestPrice != null && (
          <div className="flex flex-col items-start sm:items-end gap-1">
            <span className="text-xs opacity-90 uppercase tracking-wider">Lowest offer:</span>
            <span className="text-[1.75rem] font-bold drop-shadow-md">{formatPrice(lowestPrice)}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-5">
        <div>
          <Label htmlFor="cardmarket-condition" className="text-white/90 text-sm">
            Min. condition
          </Label>
          <Select value={condition} onValueChange={(value) => handleConditionChange(value as StandardCondition)} disabled={isLoading}>
            <SelectTrigger id="cardmarket-condition" className="h-9 w-[140px] mt-1 bg-white/10 border-white/30 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STANDARD_CONDITIONS.map((entry) => (
                <SelectItem key={entry} value={entry}>
                  {entry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button type="button" onClick={handleLoadPrices} disabled={isLoading} className="bg-white/20 border border-white/30 hover:bg-white/30">
          {isLoading ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Loading…
            </>
          ) : (
            'Load prices'
          )}
        </Button>
      </div>

      {error && <p className="text-sm text-red-200 mb-4 m-0">{error}</p>}

      {!hasLoaded && !isLoading && !error && <p className="text-center text-base opacity-90 py-4 m-0">Click &quot;Load prices&quot; to fetch current Cardmarket offers.</p>}

      {hasLoaded && !isLoading && prices?.length === 0 && <p className="text-center text-base opacity-90 py-4 m-0">No offers found for this condition.</p>}

      {hasLoaded && groupedPrices != null && groupedPrices.length > 0 && (
        <div className="rounded-lg border border-white/20 overflow-hidden bg-white/10">
          <Table>
            <TableHeader>
              <TableRow className="border-white/20 hover:bg-transparent">
                <TableHead className="text-white/90">Condition</TableHead>
                <TableHead className="text-white/90">Language</TableHead>
                <TableHead className="text-white/90 text-right">Price</TableHead>
                <TableHead className="text-white/90 text-right">Offers</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupedPrices.map((entry) => (
                <TableRow key={`${entry.condition}-${entry.language}-${entry.price}`} className="border-white/10 hover:bg-white/10">
                  <TableCell>{entry.condition}</TableCell>
                  <TableCell>{entry.language}</TableCell>
                  <TableCell className="text-right font-semibold">{formatPrice(entry.price)}</TableCell>
                  <TableCell className="text-right">{entry.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {card.cardMarketUrl && (
        <div className="flex justify-between items-center pt-4 mt-5 border-t border-white/20 flex-wrap gap-3">
          <a
            href={
              card.cardData.cardMarketUrl ? createCardmarketFilterUrl(card.cardData.cardMarketUrl, { minCondition: condition, languages: [card.set.locale] }) : card.cardMarketUrl
            }
            target="_blank"
            rel="noopener noreferrer"
            className="text-white no-underline font-semibold py-2 px-4 bg-white/20 rounded-md transition-all duration-200 text-sm border border-white/30 hover:bg-white/30 hover:translate-x-1"
          >
            View on Cardmarket →
          </a>
        </div>
      )}
    </div>
  )
}
